import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ModelCost, Model, Firm, CostItem, Workshop, CalculationType } from "../types";
import {
  costService,
  modelService,
  firmService,
  orderService,
  workshopService,
} from "../services/dataService";
import {
  formatCurrency,
  formatNumber,
  turkishIncludes,
} from "../utils/formatters";
import { useExchangeRates } from "../context/ExchangeRateContext";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./CostManagement.css";
import { usePermissions } from "../context/PermissionContext";

interface ModelCostsProps {
  /** Verilirse sayfa rota yerine bu modeli gösterir (modal içinde gömülü kullanım). */
  modelId?: string;
  onClose?: () => void;
}

const ModelCosts: React.FC<ModelCostsProps> = (props) => {
  const { has } = usePermissions(); // yetki matrisi
  const params = useParams<{ modelId?: string; firmId?: string; orderId?: string }>();
  const embedded = !!props.modelId;
  const modelId = props.modelId ?? params.modelId;
  const firmId = embedded ? undefined : params.firmId;
  const orderId = embedded ? undefined : params.orderId;
  // Gömülü kullanımda her sipariş setine kalem eklenebilir; hedef sipariş burada tutulur
  const [addOrderId, setAddOrderId] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { usdRate, eurRate, gbpRate } = useExchangeRates();

  // State
  const [firms, setFirms] = useState<Firm[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [model, setModel] = useState<Model | null>(null);
  const [modelCosts, setModelCosts] = useState<ModelCost[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ModelCost | null>(null);
  const [firmSearchTerm, setFirmSearchTerm] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [costItems, setCostItems] = useState<CostItem[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  // Confirm Modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "danger";
    onConfirm: () => void;
  }>({ isOpen: false, message: "", type: "warning", onConfirm: () => {} });

  useEffect(() => {
    if (modelId) {
      // Model detayı görünümü
      loadModel();
      loadModelCosts();
      loadCostItems();
      if (orderId || embedded) {
        loadWorkshops();
      }
    } else if (firmId) {
      // Firma modelleri görünümü
      loadFirmModels();
    } else {
      // Firma listesi görünümü
      loadFirms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, firmId, orderId]);

  const loadCostItems = async () => {
    try {
      const data = await costService.getCostItems();
      setCostItems(data);
    } catch (error) {
      console.error("❌ Failed to load cost items:", error);
    }
  };

  const loadWorkshops = async () => {
    try {
      const data = await workshopService.getAll();
      setWorkshops(data);
    } catch (error) {
      console.error("❌ Failed to load workshops:", error);
    }
  };

  const loadFirms = async () => {
    try {
      setLoading(true);
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("❌ Failed to load firms:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadFirmModels = async () => {
    if (!firmId) return;
    try {
      setLoading(true);
      const [allModels, allFirms] = await Promise.all([
        modelService.getAll(),
        firmService.getFirms(),
      ]);
      const firmModels = allModels.filter((m) => m.firmId === firmId);
      setModels(firmModels);
      setFirms(allFirms);
    } catch (error) {
      console.error("❌ Failed to load firm models:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadModel = async () => {
    if (!modelId) return;
    try {
      const [allModels, allFirms] = await Promise.all([
        modelService.getAll(),
        firmService.getFirms(),
      ]);
      const found = allModels.find((m) => m.modelId === modelId);
      setModel(found || null);
      setFirms(allFirms);
    } catch (error) {
      console.error("❌ Failed to load model:", error);
    }
  };

  const loadModelCosts = async () => {
    if (!modelId) return;
    try {
      setLoading(true);
      // orderId varsa siparişe göre, yoksa modele göre (siparişler arası tüm maliyetler)
      let data = orderId
        ? await costService.getModelCosts(orderId)
        : await costService.getModelCostsByModel(modelId!);

      setModelCosts(data);

      // Başlangıçta tüm order'lar kapalı olsun
      setExpandedOrders(new Set());
    } catch (error: any) {
      console.error("❌ Failed to load model costs:", error);
      setModelCosts([]);
      // CORS veya network hatası için daha açıklayıcı mesaj
      if (
        error.message?.includes("Failed to fetch") ||
        error.message?.includes("CORS")
      ) {
        console.warn(
          "⚠️ Model maliyetleri yüklenemedi (Bağlantı hatası). Boş liste gösteriliyor.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: ModelCost) => {
    setConfirmModal({
      isOpen: true,
      message: `"${item.costItemName || "Bu"}" maliyet kalemini bu modelden kaldırmak istediğinizden emin misiniz?`,
      type: "danger",
      onConfirm: async () => {
        try {
          setLoading(true);
          await costService.deleteModelCost(item.modelCostId);
          setAlertModal({
            isOpen: true,
            message: "Model maliyeti başarıyla kaldırıldı!",
            type: "success",
          });
          loadModelCosts();
        } catch (error: any) {
          console.error("❌ Failed to delete model cost:", error);
          setAlertModal({
            isOpen: true,
            message: "Model maliyeti kaldırılamadı!",
            type: "error",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleEdit = (item: ModelCost) => {
    // CostItem'dan quantity label'ları al
    const costItem = costItems.find((ci) => ci.costItemId === item.costItemId);
    const enrichedItem = {
      ...item,
      quantity1Label: item.quantity1Label || costItem?.quantity1Label,
      quantity2Label: item.quantity2Label || costItem?.quantity2Label,
    };
    setEditingItem(enrichedItem);
    setShowEditModal(true);
  };

  const handleUpdateModelCost = async (updatedItem: ModelCost) => {
    try {
      setLoading(true);
      await costService.updateModelCost(updatedItem.modelCostId, updatedItem);
      setAlertModal({
        isOpen: true,
        message: "Model maliyeti başarıyla güncellendi!",
        type: "success",
      });
      setShowEditModal(false);
      setEditingItem(null);
      loadModelCosts();
    } catch (error: any) {
      console.error("❌ Failed to update model cost:", error);
      setAlertModal({
        isOpen: true,
        message: "Model maliyeti güncellenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddModelCost = async (newCost: Partial<ModelCost>) => {
    const targetOrderId = orderId || addOrderId;
    if (!modelId || !targetOrderId) {
      setAlertModal({
        isOpen: true,
        message: "Model ve sipariş bilgisi gereklidir!",
        type: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      const modelCostData = {
        ...newCost,
        modelId,
        orderId: targetOrderId,
        usdRate: usdRate || undefined,
        eurRate: eurRate || undefined,
        gbpRate: gbpRate || undefined,
        exchangeRateDate: new Date().toISOString(),
        isActive: true,
      };

      await costService.addModelCost(
        modelCostData as Omit<ModelCost, "modelCostId">,
      );
      setAlertModal({
        isOpen: true,
        message: "✅ Maliyet başarıyla eklendi!",
        type: "success",
      });
      setShowAddModal(false);
      loadModelCosts();
    } catch (error: any) {
      console.error("❌ Failed to add model cost:", error);
      setAlertModal({
        isOpen: true,
        message:
          "❌ Maliyet eklenemedi: " + (error.message || "Bilinmeyen hata"),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Döviz dönüşümü fonksiyonu
  const convertToTRY = (
    amount: number,
    currency: string,
    exchangeRates: { usdRate?: number; eurRate?: number; gbpRate?: number },
  ): number => {
    if (currency === "TRY") return amount;

    const { usdRate, eurRate, gbpRate } = exchangeRates;

    switch (currency) {
      case "USD":
        if (!usdRate) {
          console.warn("⚠️ USD kuru bulunamadı, dönüşüm yapılamıyor");
          return 0;
        }
        return amount * usdRate;
      case "EUR":
        if (!eurRate) {
          console.warn("⚠️ EUR kuru bulunamadı, dönüşüm yapılamıyor");
          return 0;
        }
        return amount * eurRate;
      case "GBP":
        if (!gbpRate) {
          console.warn("⚠️ GBP kuru bulunamadı, dönüşüm yapılamıyor");
          return 0;
        }
        return amount * gbpRate;
      default:
        return amount;
    }
  };

  // Order'lara göre grupla
  const groupByOrder = () => {
    const grouped = new Map<string, ModelCost[]>();

    modelCosts.forEach((cost) => {
      const orderKey = cost.orderId || "general";
      if (!grouped.has(orderKey)) {
        grouped.set(orderKey, []);
      }
      grouped.get(orderKey)!.push(cost);
    });

    return grouped;
  };

  // Her grup için toplam maliyeti TL'ye çevirerek hesapla
  const calculateGroupTotal = (costs: ModelCost[]): number => {
    return costs.reduce((sum, item) => {
      const itemTotal = item.totalCost || 0;
      const tryAmount = convertToTRY(itemTotal, item.currency || "TRY", {
        usdRate: item.usdRate,
        eurRate: item.eurRate,
        gbpRate: item.gbpRate,
      });
      return sum + tryAmount;
    }, 0);
  };

  const toggleOrderExpanded = (orderKey: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderKey)) {
      newExpanded.delete(orderKey);
    } else {
      newExpanded.add(orderKey);
    }
    setExpandedOrders(newExpanded);
  };

  const toggleAllOrders = () => {
    const allOrderKeys = Array.from(groupByOrder().keys());
    if (expandedOrders.size === allOrderKeys.length) {
      // Tümü açık, hepsini kapat
      setExpandedOrders(new Set());
    } else {
      // Tümünü aç
      setExpandedOrders(new Set(allOrderKeys));
    }
  };

  // ============================================
  // RENDER: Firma Listesi Görünümü
  // ============================================
  if (!firmId && !modelId) {
    if (loading) {
      return (
        <div className="cost-management-container">
          <div className="loading">Yükleniyor...</div>
        </div>
      );
    }

    const filteredFirms = firms.filter(
      (firm) =>
        turkishIncludes(firm.firmName, firmSearchTerm) ||
        turkishIncludes(firm.firmCode, firmSearchTerm) ||
        turkishIncludes(firm.contactPerson || "", firmSearchTerm) ||
        turkishIncludes(firm.phone || "", firmSearchTerm) ||
        turkishIncludes(firm.email || "", firmSearchTerm),
    );

    return (
      <div className="cost-management-container">
        <div className="cost-management-header">
          <div className="header-content">
            <h1
              style={{
                margin: 0,
                fontSize: "1.75rem",
                fontWeight: "700",
                color: "#1a1a1a",
              }}
            >
              Firma Bazlı Model Maliyetleri
            </h1>
            <p
              style={{
                margin: "0.5rem 0 0",
                fontSize: "0.875rem",
                color: "#666",
              }}
            >
              Firma seçerek modellere ulaşın ({filteredFirms.length}/
              {firms.length} firma)
            </p>
          </div>
        </div>

        <div style={{ padding: "1.5rem" }}>
          {/* Arama */}
          <div
            style={{
              marginBottom: "1.25rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ position: "relative", width: "25rem" }}>
              <input
                type="text"
                placeholder="Firma ara... (kod, ad, iletişim)"
                value={firmSearchTerm}
                onChange={(e) => setFirmSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem 1rem 0.75rem 2.625rem",
                  fontSize: "0.875rem",
                  border: "0.125rem solid #e0e0e0",
                  borderRadius: "0.5rem",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#24365C")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
              />
              <svg
                style={{
                  position: "absolute",
                  left: "0.875rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#999"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </div>
          </div>
          <div
            style={{
              background: "white",
              borderRadius: "0.75rem",
              boxShadow: "0 0.125rem 0.5rem rgba(0, 0, 0, 0.08)",
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr
                  style={{
                    background:
                      "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
                    color: "white",
                  }}
                >
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      fontWeight: "600",
                      fontSize: "0.875rem",
                    }}
                  >
                    Firma Kodu
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      fontWeight: "600",
                      fontSize: "0.875rem",
                    }}
                  >
                    Firma Adı
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "left",
                      fontWeight: "600",
                      fontSize: "0.875rem",
                    }}
                  >
                    İletişim
                  </th>
                  <th
                    style={{
                      padding: "1rem",
                      textAlign: "right",
                      fontWeight: "600",
                      fontSize: "0.875rem",
                    }}
                  >
                    İşlem
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFirms.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        padding: "2.5rem",
                        textAlign: "center",
                        color: "#666",
                        fontSize: "0.875rem",
                      }}
                    >
                      Arama kriterlerine uygun firma bulunamadı.
                    </td>
                  </tr>
                ) : (
                  filteredFirms.map((firm, index) => (
                    <tr
                      key={firm.firmId}
                      onClick={() =>
                        navigate(`/model-costs/firm/${firm.firmId}`)
                      }
                      style={{
                        cursor: "pointer",
                        background: index % 2 === 0 ? "white" : "#f9fafb",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f0f4ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          index % 2 === 0 ? "white" : "#f9fafb";
                      }}
                    >
                      <td
                        style={{
                          padding: "1rem",
                          borderTop: "1px solid #e8e8e8",
                          fontSize: "0.875rem",
                          color: "#666",
                          fontWeight: "500",
                        }}
                      >
                        {firm.firmCode}
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          borderTop: "1px solid #e8e8e8",
                          fontSize: "0.9375rem",
                          color: "#1a1a1a",
                          fontWeight: "600",
                        }}
                      >
                        {firm.firmName}
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          borderTop: "1px solid #e8e8e8",
                          fontSize: "0.8125rem",
                          color: "#666",
                        }}
                      >
                        {firm.phone || firm.email || "-"}
                      </td>
                      <td
                        style={{
                          padding: "1rem",
                          borderTop: "1px solid #e8e8e8",
                          textAlign: "right",
                        }}
                      >
                        <button
                          style={{
                            padding: "0.5rem 1rem",
                            background: "#24365C",
                            color: "white",
                            border: "none",
                            borderRadius: "0.375rem",
                            fontSize: "0.8125rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#5568d3";
                            e.currentTarget.style.transform =
                              "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#24365C";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          Modelleri Gör →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Firma Modelleri Görünümü
  // ============================================
  if (firmId && !modelId) {
    if (loading) {
      return (
        <div className="cost-management-container">
          <div className="loading">Yükleniyor...</div>
        </div>
      );
    }

    const currentFirm = firms.find((f) => f.firmId === firmId);

    return (
      <div className="cost-management-container">
        <div className="cost-management-header">
          <div className="header-content">
            {/* Breadcrumb */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.8125rem",
                color: "#666",
                marginBottom: "1rem",
              }}
            >
              <span
                onClick={() => navigate("/model-costs")}
                style={{
                  cursor: "pointer",
                  color: "#24365C",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
              >
                Firmalar
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
              <span style={{ color: "#333", fontWeight: "500" }}>
                {currentFirm?.firmName || "Firma"}
              </span>
            </div>

            <h1
              style={{
                margin: 0,
                fontSize: "1.75rem",
                fontWeight: "700",
                color: "#1a1a1a",
              }}
            >
              📦 {currentFirm?.firmName} - Modeller
            </h1>
            <p
              style={{
                margin: "0.5rem 0 0",
                fontSize: "0.875rem",
                color: "#666",
              }}
            >
              Model seçerek maliyet detaylarına ulaşın ({models.length} model)
            </p>
          </div>
        </div>

        <div style={{ padding: "1.5rem" }}>
          {/* Arama */}
          <div
            style={{
              marginBottom: "1.25rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ position: "relative", width: "25rem" }}>
              <input
                type="text"
                placeholder="Model ara... (kod, ad, kategori, sezon)"
                value={modelSearchTerm}
                onChange={(e) => setModelSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.75rem 1rem 0.75rem 2.625rem",
                  fontSize: "0.875rem",
                  border: "0.125rem solid #e0e0e0",
                  borderRadius: "0.5rem",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#24365C")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
              />
              <svg
                style={{
                  position: "absolute",
                  left: "0.875rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#999"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </div>
          </div>
          {models.length === 0 ? (
            <div
              style={{
                background: "white",
                borderRadius: "0.75rem",
                padding: "2.5rem",
                textAlign: "center",
                color: "#666",
                boxShadow: "0 0.125rem 0.5rem rgba(0, 0, 0, 0.08)",
              }}
            >
              Bu firmaya ait model bulunmuyor.
            </div>
          ) : (
            (() => {
              const filteredModels = models.filter(
                (model) =>
                  turkishIncludes(model.modelName, modelSearchTerm) ||
                  turkishIncludes(model.modelCode, modelSearchTerm) ||
                  turkishIncludes(model.category || "", modelSearchTerm) ||
                  turkishIncludes(model.season || "", modelSearchTerm),
              );

              return (
                <div
                  style={{
                    background: "white",
                    borderRadius: "0.75rem",
                    boxShadow: "0 0.125rem 0.5rem rgba(0, 0, 0, 0.08)",
                    overflow: "hidden",
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background:
                            "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
                          color: "white",
                        }}
                      >
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "0.875rem",
                          }}
                        >
                          Model Kodu
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "0.875rem",
                          }}
                        >
                          Model Adı
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "0.875rem",
                          }}
                        >
                          Kategori
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "0.875rem",
                          }}
                        >
                          Sezon
                        </th>
                        <th
                          style={{
                            padding: "1rem",
                            textAlign: "right",
                            fontWeight: "600",
                            fontSize: "0.875rem",
                          }}
                        >
                          İşlem
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredModels.length === 0 ? (
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              padding: "2.5rem",
                              textAlign: "center",
                              color: "#666",
                              fontSize: "0.875rem",
                            }}
                          >
                            Arama kriterlerine uygun model bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        filteredModels.map((model, index) => (
                          <tr
                            key={model.modelId}
                            onClick={() =>
                              navigate(`/model-costs/model/${model.modelId}`)
                            }
                            style={{
                              cursor: "pointer",
                              background: index % 2 === 0 ? "white" : "#f9fafb",
                              transition: "background-color 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#f0f4ff";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                index % 2 === 0 ? "white" : "#f9fafb";
                            }}
                          >
                            <td
                              style={{
                                padding: "1rem",
                                borderTop: "1px solid #e8e8e8",
                                fontSize: "0.875rem",
                                color: "#666",
                                fontWeight: "500",
                              }}
                            >
                              {model.modelCode}
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                borderTop: "1px solid #e8e8e8",
                                fontSize: "0.9375rem",
                                color: "#1a1a1a",
                                fontWeight: "600",
                              }}
                            >
                              👔 {model.modelName}
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                borderTop: "1px solid #e8e8e8",
                                fontSize: "0.8125rem",
                                color: "#666",
                              }}
                            >
                              {model.category || "-"}
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                borderTop: "1px solid #e8e8e8",
                                fontSize: "0.8125rem",
                                color: "#666",
                              }}
                            >
                              {model.season || "-"}
                            </td>
                            <td
                              style={{
                                padding: "1rem",
                                borderTop: "1px solid #e8e8e8",
                                textAlign: "right",
                              }}
                            >
                              <button
                                style={{
                                  padding: "0.5rem 1rem",
                                  background: "#24365C",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "0.375rem",
                                  fontSize: "0.8125rem",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#5568d3";
                                  e.currentTarget.style.transform =
                                    "translateY(-1px)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#24365C";
                                  e.currentTarget.style.transform =
                                    "translateY(0)";
                                }}
                              >
                                Maliyetleri Gör →
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Model Detayı ve Maliyetler
  // ============================================
  if (loading && modelCosts.length === 0) {
    return (
      <div className="cost-management-container">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (!loading && modelCosts.length === 0 && modelId) {
    const modelFirmId = model?.firmId;
    const currentFirm = firms.find((f) => f.firmId === modelFirmId);

    return (
      <div className="cost-management-container">
        <div className="cost-management-header">
          <div className="header-content">
            {/* Breadcrumb */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.8125rem",
                color: "#666",
                marginBottom: "1rem",
              }}
            >
              <span
                onClick={() => navigate("/model-costs")}
                style={{
                  cursor: "pointer",
                  color: "#24365C",
                  fontWeight: "500",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.textDecoration = "underline")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.textDecoration = "none")
                }
              >
                Firmalar
              </span>
              {modelFirmId && (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </svg>
                  <span
                    onClick={() => navigate(`/model-costs/firm/${modelFirmId}`)}
                    style={{
                      cursor: "pointer",
                      color: "#24365C",
                      fontWeight: "500",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    {currentFirm?.firmName || "Firma"}
                  </span>
                </>
              )}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
              <span style={{ color: "#333", fontWeight: "500" }}>
                {model?.modelName || "Model"}
              </span>
            </div>

            <h1>{model?.modelName || "Model"} - Maliyet Yönetimi</h1>
          </div>
        </div>
        <div style={{ padding: "2.5rem", textAlign: "center", color: "#666" }}>
          <p>Bu model için henüz maliyet kaydı bulunmuyor.</p>
          <p style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
            Sipariş oluşturulduğunda maliyetler otomatik olarak burada
            görünecektir.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cost-management-container">
      <div className="cost-management-header">
        <div className="header-content">
          {/* Breadcrumb */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontSize: "0.8125rem",
              color: "#666",
              marginBottom: "1rem",
            }}
          >
            <span
              onClick={() => navigate("/model-costs")}
              style={{
                cursor: "pointer",
                color: "#24365C",
                fontWeight: "500",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.textDecoration = "underline")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.textDecoration = "none")
              }
            >
              Firmalar
            </span>
            {model?.firmId && (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
                <span
                  onClick={() => navigate(`/model-costs/firm/${model.firmId}`)}
                  style={{
                    cursor: "pointer",
                    color: "#24365C",
                    fontWeight: "500",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.textDecoration = "underline")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.textDecoration = "none")
                  }
                >
                  {firms.find((f) => f.firmId === model.firmId)?.firmName ||
                    "Firma"}
                </span>
              </>
            )}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#999">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
            <span style={{ color: "#333", fontWeight: "500" }}>
              {model?.modelName || "Model"}
            </span>
          </div>

          {/* Başlık */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h1 style={{ margin: 0 }}>
              {model?.modelName || "Model"} - Maliyet Yönetimi
            </h1>
          </div>
        </div>
      </div>
      {/* Order Bazlı Gruplama */}
      <div className="orders-container" style={{ marginTop: "1.5rem" }}>
        {/* Tümünü Aç/Kapat Butonu */}
        {Array.from(groupByOrder()).length > 1 && (
          <div style={{ marginBottom: "1.25rem", textAlign: "right" }}>
            <button
              onClick={toggleAllOrders}
              style={{
                padding: "0.625rem 1.125rem",
                background: "white",
                color: "#24365C",
                border: "0.125rem solid #24365C",
                borderRadius: "0.5rem",
                cursor: "pointer",
                fontSize: "0.8125rem",
                fontWeight: "600",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s ease",
                boxShadow: "0 0.125rem 0.375rem rgba(36, 54, 92, 0.15)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#24365C";
                e.currentTarget.style.color = "white";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 0.25rem 0.75rem rgba(36, 54, 92, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "white";
                e.currentTarget.style.color = "#24365C";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 0.125rem 0.375rem rgba(36, 54, 92, 0.15)";
              }}
            >
              {expandedOrders.size === Array.from(groupByOrder()).length ? (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="18 15 12 9 6 15" />
                  </svg>
                  Tümünü Kapat
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                  Tümünü Aç
                </>
              )}
            </button>
          </div>
        )}

        {Array.from(groupByOrder()).map(([orderKey, costs]) => {
          const firstCost = costs[0];
          const groupTotal = calculateGroupTotal(costs);
          const orderQuantity = firstCost?.orderQuantity || 0;
          const unitCost = orderQuantity > 0 ? groupTotal / orderQuantity : 0;

          return (
            <div
              key={orderKey}
              style={{
                marginBottom: "1.5rem",
                background: "white",
                borderRadius: "0.75rem",
                boxShadow: expandedOrders.has(orderKey)
                  ? "0 0.25rem 1.25rem rgba(0, 0, 0, 0.1)"
                  : "0 0.125rem 0.5rem rgba(0, 0, 0, 0.08)",
                border: "1px solid #e8e8e8",
                overflow: "hidden",
                transition: "all 0.3s ease",
              }}
            >
              {/* Grup Başlığı - Tıklanabilir */}
              <div
                onClick={() => toggleOrderExpanded(orderKey)}
                style={{
                  background: expandedOrders.has(orderKey)
                    ? "linear-gradient(135deg, #24365C 0%, #101B33 100%)"
                    : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  color: expandedOrders.has(orderKey) ? "white" : "#333",
                  padding: "1.25rem 1.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  userSelect: "none",
                }}
                onMouseEnter={(e) => {
                  if (!expandedOrders.has(orderKey)) {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!expandedOrders.has(orderKey)) {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)";
                  }
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                >
                  {/* Icon Container */}
                  <div
                    style={{
                      width: "2.5rem",
                      height: "2.5rem",
                      borderRadius: "0.625rem",
                      background: expandedOrders.has(orderKey)
                        ? "rgba(255, 255, 255, 0.2)"
                        : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={
                        expandedOrders.has(orderKey) ? "white" : "#24365C"
                      }
                      strokeWidth="2"
                      style={{
                        transition: "transform 0.3s ease",
                        transform: expandedOrders.has(orderKey)
                          ? "rotate(90deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </div>
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "1.125rem",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {orderKey === "general"
                          ? "🏭 Genel Model Maliyetleri"
                          : `📦 Sipariş #${orderKey}`}
                      </span>
                      {orderQuantity > 0 && (
                        <span
                          style={{
                            fontSize: "0.9375rem",
                            fontWeight: "600",
                            background: expandedOrders.has(orderKey)
                              ? "rgba(255, 255, 255, 0.25)"
                              : "#e3f2fd",
                            color: expandedOrders.has(orderKey)
                              ? "white"
                              : "#1565c0",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "0.375rem",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.375rem",
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M20 7h-9" />
                            <path d="M14 17H5" />
                            <circle cx="17" cy="17" r="3" />
                            <circle cx="7" cy="7" r="3" />
                          </svg>
                          {orderQuantity} Adet
                        </span>
                      )}
                    </h3>
                    {orderQuantity > 0 && (
                      <>
                        <p
                          style={{
                            margin: "0.25rem 0 0",
                            opacity: 0.85,
                            fontSize: "0.8125rem",
                            fontWeight: "500",
                            display: "none", // Adet bilgisi artık başlıkta gösteriliyor
                          }}
                        >
                          Adet: {orderQuantity}
                        </p>
                      </>
                    )}
                    {/* Sipariş Tarihi - exchangeRateDate'den al */}
                    {firstCost?.exchangeRateDate && (
                      <p
                        style={{
                          margin: "0.25rem 0 0",
                          opacity: 0.85,
                          fontSize: "0.8125rem",
                          fontWeight: "500",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.375rem",
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <rect
                            x="3"
                            y="4"
                            width="18"
                            height="18"
                            rx="2"
                            ry="2"
                          />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        {new Date(
                          firstCost.exchangeRateDate,
                        ).toLocaleDateString("tr-TR", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </p>
                    )}
                    {/* Döviz Kurları - orderInfo dışında */}
                    <p
                      style={{
                        margin: "0.25rem 0 0",
                        opacity: 0.85,
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="1" x2="12" y2="23" />
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                      </svg>
                      <span>
                        Kurlar{" "}
                        {firstCost?.createdAt &&
                          `(${new Date(firstCost.createdAt).toLocaleDateString(
                            "tr-TR",
                          )})`}
                        :
                      </span>
                      <span
                        style={{
                          background: expandedOrders.has(orderKey)
                            ? "rgba(255,255,255,0.2)"
                            : "#e0f2fe",
                          color: expandedOrders.has(orderKey)
                            ? "white"
                            : "#0369a1",
                          padding: "0.125rem 0.5rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.6875rem",
                          fontWeight: "600",
                        }}
                      >
                        USD: {firstCost?.usdRate?.toFixed(4) || "Yok"}
                      </span>
                      <span
                        style={{
                          background: expandedOrders.has(orderKey)
                            ? "rgba(255,255,255,0.2)"
                            : "#fef3c7",
                          color: expandedOrders.has(orderKey)
                            ? "white"
                            : "#92400e",
                          padding: "0.125rem 0.5rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.6875rem",
                          fontWeight: "600",
                        }}
                      >
                        EUR: {firstCost?.eurRate?.toFixed(4) || "Yok"}
                      </span>
                    </p>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "0.8125rem",
                      opacity: 0.85,
                      fontWeight: "500",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {costs.length} Maliyet Kalemi
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "700",
                      letterSpacing: "-0.5px",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {formatCurrency(groupTotal, "TRY")}
                  </div>
                  {orderQuantity > 0 && (
                    <div
                      style={{
                        fontSize: "0.875rem",
                        opacity: 0.9,
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "0.375rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <span style={{ fontSize: "0.75rem", opacity: 0.8 }}>
                        Birim:
                      </span>
                      {formatCurrency(unitCost, "TRY")}
                    </div>
                  )}
                  {/* Yeni Maliyet Ekle Butonu - Sadece sipariş varsa ve yetki varsa göster */}
                  {orderKey !== "general" && (orderId || embedded) && has("model-costs:create") && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddOrderId(orderKey);
                        setShowAddModal(true);
                      }}
                      style={{
                        padding: "0.5rem 1rem",
                        background: expandedOrders.has(orderKey)
                          ? "rgba(255, 255, 255, 0.2)"
                          : "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
                        color: "white",
                        border: expandedOrders.has(orderKey)
                          ? "0.125rem solid rgba(255, 255, 255, 0.4)"
                          : "none",
                        borderRadius: "0.5rem",
                        cursor: "pointer",
                        fontSize: "0.8125rem",
                        fontWeight: "600",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.375rem",
                        transition: "all 0.2s ease",
                        boxShadow: expandedOrders.has(orderKey)
                          ? "0 0.125rem 0.5rem rgba(0, 0, 0, 0.15)"
                          : "0 0.25rem 0.75rem rgba(36, 54, 92, 0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-0.125rem)";
                        e.currentTarget.style.boxShadow =
                          "0 0.375rem 1rem rgba(36, 54, 92, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = expandedOrders.has(
                          orderKey,
                        )
                          ? "0 0.125rem 0.5rem rgba(0, 0, 0, 0.15)"
                          : "0 0.25rem 0.75rem rgba(36, 54, 92, 0.3)";
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      Yeni Maliyet Ekle
                    </button>
                  )}
                </div>
              </div>

              {/* Detaylar - Açıldığında Göster */}
              {expandedOrders.has(orderKey) && (
                <div
                  style={{
                    padding: "0",
                    animation: "slideDown 0.3s ease-out",
                    background: "#fafbfc",
                  }}
                >
                  <div
                    style={{
                      overflowX: "auto",
                      margin: "0",
                    }}
                  >
                    <table
                      className="items-table"
                      style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "#f8f9fa",
                            borderBottom: "0.125rem solid #e8e8e8",
                          }}
                        >
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "left",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Maliyet Kalemi
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "left",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Kategori
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "left",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Birim 1
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Miktar 1
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "left",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Birim 2
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Miktar 2
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            3. Boyut (Ref)
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Fire %
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Net Miktar
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "right",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Birim Fiyat
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "right",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Toplam
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "right",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            TL Karşılığı
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "left",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            Kullanım
                          </th>
                          <th
                            style={{
                              padding: "0.875rem 1rem",
                              textAlign: "center",
                              fontSize: "0.75rem",
                              fontWeight: "700",
                              color: "#24365C",
                              textTransform: "uppercase",
                              letterSpacing: "0.0312rem",
                            }}
                          >
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {costs.map((item) => {
                          const tryEquivalent = convertToTRY(
                            item.totalCost || 0,
                            item.currency || "TRY",
                            {
                              usdRate: item.usdRate,
                              eurRate: item.eurRate,
                              gbpRate: item.gbpRate,
                            },
                          );

                          return (
                            <tr key={item.modelCostId}>
                              <td>
                                <div className="item-name">
                                  <strong>{item.costItemName || "-"}</strong>
                                </div>
                              </td>
                              <td>
                                <span className="category-badge">
                                  {item.costCategoryName || "-"}
                                </span>
                              </td>
                              <td>
                                <span style={{ fontWeight: "500" }}>
                                  {item.unit || "-"}
                                </span>
                              </td>
                              <td>
                                <strong>{item.quantity}</strong>
                              </td>
                              <td>
                                {item.unit2 ? (
                                  <span style={{ fontWeight: "500" }}>
                                    {item.unit2}
                                  </span>
                                ) : (
                                  <span style={{ color: "#ccc" }}>-</span>
                                )}
                              </td>
                              <td>
                                {item.quantity2 ? (
                                  <strong>{item.quantity2}</strong>
                                ) : (
                                  <span style={{ color: "#ccc" }}>-</span>
                                )}
                              </td>
                              <td>
                                {item.quantity3 && item.unit3 ? (
                                  <span
                                    style={{
                                      color: "#856404",
                                      fontSize: "0.9em",
                                    }}
                                  >
                                    <strong>{item.quantity3}</strong>
                                    <span style={{ margin: "0 0.25rem" }}>×</span>
                                    {item.unit3}
                                  </span>
                                ) : (
                                  <span style={{ color: "#ccc" }}>-</span>
                                )}
                              </td>
                              <td>
                                {item.wastagePercentage !== undefined &&
                                item.wastagePercentage !== null ? (
                                  <span
                                    style={{
                                      color: "#d32f2f",
                                      fontWeight: "600",
                                    }}
                                  >
                                    %{item.wastagePercentage}
                                  </span>
                                ) : (
                                  <span style={{ color: "#ccc" }}>-</span>
                                )}
                              </td>
                              <td>
                                {item.actualQuantityNeeded ? (
                                  <strong style={{ color: "#2e7d32" }}>
                                    {formatNumber(item.actualQuantityNeeded)}
                                  </strong>
                                ) : (
                                  item.quantity
                                )}
                              </td>
                              <td className="price-cell">
                                {item.unitPrice &&
                                  formatCurrency(item.unitPrice, item.currency)}
                              </td>
                              <td className="price-cell">
                                <strong style={{ color: "#1976d2" }}>
                                  {item.totalCost &&
                                    formatCurrency(
                                      item.totalCost,
                                      item.currency,
                                    )}
                                </strong>
                              </td>
                              <td className="price-cell">
                                <strong style={{ color: "#2e7d32" }}>
                                  {formatCurrency(tryEquivalent, "TRY")}
                                </strong>
                                {item.currency !== "TRY" && item.usdRate && (
                                  <div
                                    style={{
                                      fontSize: "0.6875rem",
                                      color: "#666",
                                      marginTop: "0.125rem",
                                    }}
                                  >
                                    1 {item.currency} ={" "}
                                    {item.currency === "USD"
                                      ? item.usdRate
                                      : item.currency === "EUR"
                                        ? item.eurRate
                                        : item.gbpRate}{" "}
                                    TL
                                  </div>
                                )}
                              </td>
                              <td>
                                {item.order?.workshop?.name ? (
                                  <span
                                    style={{
                                      fontSize: "0.8125rem",
                                      color: "#2c3e50",
                                      fontWeight: "500",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.25rem",
                                      padding: "0.25rem 0.5rem",
                                      background: "#e8f5e9",
                                      borderRadius: "0.25rem",
                                    }}
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#2e7d32"
                                      strokeWidth="2"
                                    >
                                      <rect
                                        x="2"
                                        y="7"
                                        width="20"
                                        height="14"
                                        rx="2"
                                        ry="2"
                                      ></rect>
                                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                    </svg>
                                    {item.order.workshop.name}
                                  </span>
                                ) : item.usage ? (
                                  <span
                                    style={{ fontSize: "0.8125rem", color: "#666" }}
                                    title={item.usage}
                                  >
                                    {item.usage.substring(0, 20)}
                                    {item.usage.length > 20 ? "..." : ""}
                                  </span>
                                ) : (
                                  <span style={{ color: "#ccc" }}>-</span>
                                )}
                              </td>
                              <td>
                                <div
                                  className="action-buttons"
                                  style={{
                                    display: "flex",
                                    gap: "0.5rem",
                                    justifyContent: "center",
                                  }}
                                >
                                  {has("model-costs:update") && (
                                  <button
                                    onClick={() => handleEdit(item)}
                                    title="Düzenle"
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
                                      border: "none",
                                      padding: "0.5rem 0.875rem",
                                      borderRadius: "0.375rem",
                                      cursor: "pointer",
                                      color: "white",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.375rem",
                                      fontSize: "0.8125rem",
                                      fontWeight: "600",
                                      boxShadow:
                                        "0 0.125rem 0.5rem rgba(36, 54, 92, 0.3)",
                                      transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "translateY(-0.125rem)";
                                      e.currentTarget.style.boxShadow =
                                        "0 0.25rem 0.75rem rgba(36, 54, 92, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                      e.currentTarget.style.boxShadow =
                                        "0 0.125rem 0.5rem rgba(36, 54, 92, 0.3)";
                                    }}
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                    Düzenle
                                  </button>
                                  )}
                                  {has("model-costs:delete") && (
                                  <button
                                    onClick={() => handleDelete(item)}
                                    title="Sil"
                                    style={{
                                      background:
                                        "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                                      border: "none",
                                      padding: "0.5rem 0.875rem",
                                      borderRadius: "0.375rem",
                                      cursor: "pointer",
                                      color: "white",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "0.375rem",
                                      fontSize: "0.8125rem",
                                      fontWeight: "600",
                                      boxShadow:
                                        "0 0.125rem 0.5rem rgba(245, 87, 108, 0.3)",
                                      transition: "all 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform =
                                        "translateY(-0.125rem)";
                                      e.currentTarget.style.boxShadow =
                                        "0 0.25rem 0.75rem rgba(245, 87, 108, 0.4)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform =
                                        "translateY(0)";
                                      e.currentTarget.style.boxShadow =
                                        "0 0.125rem 0.5rem rgba(245, 87, 108, 0.3)";
                                    }}
                                  >
                                    <svg
                                      width="14"
                                      height="14"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                    Sil
                                  </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Eski tfoot ve boş mesaj - Kaldırıldı */}
      {modelCosts.length === 0 && (
        <div style={{ padding: "2.5rem", textAlign: "center", color: "#999" }}>
          <p style={{ color: "#999", fontSize: "1rem" }}>
            Bu model için henüz maliyet kalemi eklenmemiş.
          </p>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditModelCostModal
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={handleUpdateModelCost}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (orderId || addOrderId) && modelId && (
        <AddModelCostModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddModelCost}
          costItems={costItems}
          workshops={workshops}
        />
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={() => {
          confirmModal.onConfirm();
          setConfirmModal({ ...confirmModal, isOpen: false });
        }}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
};

// Edit Modal Component
interface EditModalProps {
  item: ModelCost;
  onClose: () => void;
  onSave: (item: ModelCost) => void;
}

const EditModelCostModal: React.FC<EditModalProps> = ({
  item,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<ModelCost>(item);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (field: keyof ModelCost, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(0.25rem)",
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "37.5rem",
          width: "90%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "white",
          borderRadius: "1rem",
          boxShadow: "0 0.625rem 2.5rem rgba(0, 0, 0, 0.2)",
        }}
      >
        <div
          className="modal-header"
          style={{
            padding: "1.5rem",
            borderBottom: "1px solid #e8e8e8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: "600",
              color: "#1a1a1a",
            }}
          >
            Miktar Düzenle
          </h2>
          <button
            className="modal-close"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.75rem",
              cursor: "pointer",
              color: "#999",
              lineHeight: "1",
              padding: "0",
              width: "2rem",
              height: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "0.375rem",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f5f5f5";
              e.currentTarget.style.color = "#333";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "none";
              e.currentTarget.style.color = "#999";
            }}
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div
            className="modal-body"
            style={{
              padding: "1.5rem",
            }}
          >
            {/* Maliyet Kalemi Bilgisi - Sadece Gösterim */}
            <div
              style={{
                background: "#f8f9fa",
                padding: "1rem",
                borderRadius: "0.5rem",
                marginBottom: "1.5rem",
                border: "1px solid #e9ecef",
              }}
            >
              <div style={{ marginBottom: "0.5rem" }}>
                <span
                  style={{ fontSize: "0.8125rem", color: "#666", fontWeight: "500" }}
                >
                  Maliyet Kalemi:
                </span>
                <span
                  style={{
                    marginLeft: "0.5rem",
                    fontSize: "0.875rem",
                    color: "#1a1a1a",
                    fontWeight: "600",
                  }}
                >
                  {formData.costItemName}
                </span>
              </div>
              <div style={{ marginBottom: "0.5rem" }}>
                <span
                  style={{ fontSize: "0.8125rem", color: "#666", fontWeight: "500" }}
                >
                  Kategori:
                </span>
                <span
                  style={{
                    marginLeft: "0.5rem",
                    fontSize: "0.875rem",
                    color: "#1a1a1a",
                  }}
                >
                  {formData.costCategoryName}
                </span>
              </div>
              <div>
                <span
                  style={{ fontSize: "0.8125rem", color: "#666", fontWeight: "500" }}
                >
                  Birim Fiyat:
                </span>
                <span
                  style={{
                    marginLeft: "0.5rem",
                    fontSize: "0.875rem",
                    color: "#1a1a1a",
                    fontWeight: "600",
                  }}
                >
                  {formData.unitPrice} {formData.currency}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              {/* Quantity 1 - Always shown */}
              <div className="form-group">
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  {formData.quantity1Label || "Miktar"} *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity || ""}
                  onChange={(e) =>
                    handleChange("quantity", parseFloat(e.target.value) || 0)
                  }
                  required
                  style={{
                    width: "100%",
                    padding: "0.625rem 0.75rem",
                    fontSize: "0.875rem",
                    border: "0.125rem solid #e0e0e0",
                    borderRadius: "0.5rem",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#24365C")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e0e0e0")
                  }
                />
              </div>
              <div className="form-group">
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Birim
                </label>
                <input
                  type="text"
                  value={formData.unit || ""}
                  readOnly
                  disabled
                  style={{
                    width: "100%",
                    padding: "0.625rem 0.75rem",
                    fontSize: "0.875rem",
                    border: "0.125rem solid #e8e8e8",
                    borderRadius: "0.5rem",
                    background: "#f5f5f5",
                    color: "#999",
                    cursor: "not-allowed",
                  }}
                />
              </div>

              {/* Quantity 1 Label - Editable */}
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#333",
                  }}
                >
                  Miktar 1 Etiketi (isteğe bağlı)
                </label>
                <input
                  type="text"
                  value={formData.quantity1Label || ""}
                  onChange={(e) =>
                    handleChange("quantity1Label", e.target.value || undefined)
                  }
                  placeholder="Örn: En, Boy, Adet, vb."
                  style={{
                    width: "100%",
                    padding: "0.625rem 0.75rem",
                    fontSize: "0.875rem",
                    border: "0.125rem solid #e0e0e0",
                    borderRadius: "0.5rem",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                  }}
                  onFocus={(e) =>
                    (e.currentTarget.style.borderColor = "#24365C")
                  }
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = "#e0e0e0")
                  }
                />
              </div>

              {/* Quantity 2 - Only if exists */}
              {formData.quantity2 !== undefined &&
                formData.quantity2 !== null && (
                  <>
                    <div className="form-group">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        {formData.quantity2Label || "Miktar 2"}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.quantity2 || ""}
                        onChange={(e) =>
                          handleChange(
                            "quantity2",
                            parseFloat(e.target.value) || undefined,
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "0.625rem 0.75rem",
                          fontSize: "0.875rem",
                          border: "0.125rem solid #e0e0e0",
                          borderRadius: "0.5rem",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.currentTarget.style.borderColor = "#24365C")
                        }
                        onBlur={(e) =>
                          (e.currentTarget.style.borderColor = "#e0e0e0")
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Birim 2
                      </label>
                      <input
                        type="text"
                        value={formData.unit2 || ""}
                        readOnly
                        disabled
                        style={{
                          width: "100%",
                          padding: "0.625rem 0.75rem",
                          fontSize: "0.875rem",
                          border: "0.125rem solid #e8e8e8",
                          borderRadius: "0.5rem",
                          background: "#f5f5f5",
                          color: "#999",
                          cursor: "not-allowed",
                        }}
                      />
                    </div>
                    {/* Quantity 2 Label - Editable */}
                    <div
                      className="form-group"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Miktar 2 Etiketi (isteğe bağlı)
                      </label>
                      <input
                        type="text"
                        value={formData.quantity2Label || ""}
                        onChange={(e) =>
                          handleChange(
                            "quantity2Label",
                            e.target.value || undefined,
                          )
                        }
                        placeholder="Örn: Çeşit Sayısı, Renk Sayısı, vb."
                        style={{
                          width: "100%",
                          padding: "0.625rem 0.75rem",
                          fontSize: "0.875rem",
                          border: "0.125rem solid #e0e0e0",
                          borderRadius: "0.5rem",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.currentTarget.style.borderColor = "#24365C")
                        }
                        onBlur={(e) =>
                          (e.currentTarget.style.borderColor = "#e0e0e0")
                        }
                      />
                    </div>
                  </>
                )}

              {/* Quantity 3 - Only if exists */}
              {formData.quantity3 !== undefined &&
                formData.quantity3 !== null && (
                  <>
                    <div className="form-group">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Miktar 3 (Ref)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.quantity3 || ""}
                        onChange={(e) =>
                          handleChange(
                            "quantity3",
                            parseFloat(e.target.value) || undefined,
                          )
                        }
                        style={{
                          width: "100%",
                          padding: "0.625rem 0.75rem",
                          fontSize: "0.875rem",
                          border: "0.125rem solid #e0e0e0",
                          borderRadius: "0.5rem",
                          outline: "none",
                          transition: "border-color 0.2s ease",
                        }}
                        onFocus={(e) =>
                          (e.currentTarget.style.borderColor = "#24365C")
                        }
                        onBlur={(e) =>
                          (e.currentTarget.style.borderColor = "#e0e0e0")
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          fontSize: "0.875rem",
                          fontWeight: "500",
                          color: "#333",
                        }}
                      >
                        Birim 3 (Ref)
                      </label>
                      <input
                        type="text"
                        value={formData.unit3 || ""}
                        readOnly
                        disabled
                        style={{
                          width: "100%",
                          padding: "0.625rem 0.75rem",
                          fontSize: "0.875rem",
                          border: "0.125rem solid #e8e8e8",
                          borderRadius: "0.5rem",
                          background: "#f5f5f5",
                          color: "#999",
                          cursor: "not-allowed",
                        }}
                      />
                    </div>
                  </>
                )}
            </div>
          </div>
          <div
            className="modal-footer"
            style={{
              padding: "1rem 1.5rem",
              borderTop: "1px solid #e8e8e8",
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{
                padding: "0.625rem 1.5rem",
                fontSize: "0.875rem",
                fontWeight: "600",
                border: "0.125rem solid #e0e0e0",
                background: "white",
                color: "#666",
                borderRadius: "0.5rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#999";
                e.currentTarget.style.color = "#333";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e0e0e0";
                e.currentTarget.style.color = "#666";
              }}
            >
              İptal
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              style={{
                padding: "0.625rem 1.5rem",
                fontSize: "0.875rem",
                fontWeight: "600",
                border: "none",
                background: "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
                color: "white",
                borderRadius: "0.5rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 0.25rem 0.75rem rgba(36, 54, 92, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-0.125rem)";
                e.currentTarget.style.boxShadow =
                  "0 0.375rem 1rem rgba(36, 54, 92, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 0.25rem 0.75rem rgba(36, 54, 92, 0.3)";
              }}
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Modal Component
interface AddModalProps {
  onClose: () => void;
  onSave: (cost: Partial<ModelCost>) => void;
  costItems: CostItem[];
  workshops: Workshop[];
}

// Form verisi için özel tip - ModelCost'a ek olarak label alanları içeriyor
interface ModelCostFormData extends Partial<ModelCost> {
  quantity1Label?: string;
  quantity2Label?: string;
}

const AddModelCostModal: React.FC<AddModalProps> = ({
  onClose,
  onSave,
  costItems,
  workshops,
}) => {
  const [formData, setFormData] = useState<ModelCostFormData>({
    costItemId: "",
    quantity: 0,
    unit: "",
    quantity2: undefined,
    unit2: "",
    unitPrice: 0,
    totalCost: 0,
    currency: "TRY",
    usage: "",
    notes: "",
    wastagePercentage: 0,
    quantity1Label: "",
    quantity2Label: "",
  });

  const [selectedCostItem, setSelectedCostItem] = useState<CostItem | null>(
    null,
  );
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>("");
  const [workshopCostItems, setWorkshopCostItems] = useState<CostItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Atölye seçildiğinde o atölyeye ait maliyet kalemlerini yükle
  const handleWorkshopChange = async (workshopId: string) => {
    setSelectedWorkshopId(workshopId);
    const selectedWorkshop = workshops.find((w) => w.workshopId === workshopId);

    setFormData((prev) => ({
      ...prev,
      usage: selectedWorkshop?.name || "",
      costItemId: "", // Maliyet kalemini sıfırla
    }));

    if (workshopId) {
      try {
        setLoadingItems(true);
        const workshopItems =
          await costService.getWorkshopCostItems(workshopId);

        // WorkshopCostItem'ları CostItem formatına dönüştür
        // API flat bir yapı döndürüyor, nested değil
        const items: CostItem[] = workshopItems
          .filter((wi) => wi.costItemId) // costItemId null olanları filtrele
          .map((wi) => ({
            costItemId: wi.costItemId, // Flat yapıda direkt costItemId
            costCategoryId: "", // API'de yok
            itemName: wi.costItemName || "", // costItemName
            description: wi.costItemDescription,
            unit: wi.unitCode,
            unitName: wi.quantity1Label, // quantity1Label birim adı gibi
            unit2: wi.unitCode2,
            unitPrice: wi.effectivePrice || wi.standardPrice || 0,
            currency: wi.effectiveCurrency || wi.standardCurrency || "TRY",
            supplier: undefined,
            wastagePercentage: undefined,
            minimumOrderQuantity: undefined,
            discountPercentage: wi.workshopDiscountPercentage || undefined,
            lastPriceUpdate: new Date().toISOString(),
            isActive: true,
            calculationType: wi.calculationType,
            materialWidth: wi.materialWidth,
            materialHeight: wi.materialHeight,
            quantity1Label: wi.quantity1Label,
            quantity2Label: wi.quantity2Label,
            quantity3Label: wi.quantity3Label,
            costCategory: {
              costCategoryId: "",
              category: wi.categoryName || "", // 'category' alanı zorunlu
              categoryName: wi.categoryName || "",
              description: undefined,
              displayOrder: undefined,
              isActive: true,
            },
          }));

        setWorkshopCostItems(items);
      } catch (error) {
        console.error("❌ Atölye maliyet kalemleri yüklenemedi:", error);
        // Alert yerine console.error kullanıldı (modal içindeyiz)
        setWorkshopCostItems([]);
      } finally {
        setLoadingItems(false);
      }
    } else {
      setWorkshopCostItems([]);
    }
  };

  const handleCostItemChange = (costItemId: string) => {
    const item = workshopCostItems.find((i) => i.costItemId === costItemId);
    setSelectedCostItem(item || null);

    setFormData((prev) => ({
      ...prev,
      costItemId,
      // Birim fiyat ve para birimi costItem'dan alınıyor, güncellenmiyor
      unitPrice: item?.unitPrice || 0,
      currency: item?.currency || "TRY",
      unit: item?.unitName || item?.unit || "",
      unit2: item?.unit2 || "",
      quantity1Label: item?.quantity1Label,
      quantity2Label: item?.quantity2Label,
    }));
  };

  const calculateTotal = () => {
    const qty1 = formData.quantity || 0;
    // Boya hesaplamada Çeşit Sayısı (quantity2) yalnız bilgi amaçlı — çarpana girmez
    const isPaintBased =
      selectedCostItem?.calculationType === CalculationType.PaintBased;
    const qty2 = isPaintBased ? 1 : formData.quantity2 || 1;
    const price = formData.unitPrice || 0;
    const wastage = formData.wastagePercentage || 0;

    let total = qty1 * qty2 * price;
    if (wastage > 0) {
      total = total * (1 + wastage / 100);
    }

    setFormData((prev) => ({ ...prev, totalCost: total }));
  };

  useEffect(() => {
    calculateTotal();
  }, [
    formData.quantity,
    formData.quantity2,
    formData.unitPrice,
    formData.wastagePercentage,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedWorkshopId) {
      // Alert yerine console.warn kullanıldı (modal içindeyiz, validation inline yapılmalı)
      console.warn("Lütfen bir atölye seçin!");
      return;
    }

    if (!formData.costItemId) {
      console.warn("Lütfen bir maliyet kalemi seçin!");
      return;
    }

    if (!formData.quantity || formData.quantity <= 0) {
      console.warn("Lütfen geçerli bir miktar girin!");
      return;
    }

    // Label alanlarını ModelCost'a göndermeden önce çıkar
    const { quantity1Label, quantity2Label, ...modelCostData } = formData;
    onSave(modelCostData);
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1.25rem",
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "1rem",
          width: "100%",
          maxWidth: "50rem",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 1.25rem 3.75rem rgba(0, 0, 0, 0.3)",
        }}
      >
        <div
          style={{
            padding: "1.5rem 2rem",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
            borderRadius: "1rem 1rem 0 0",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "1.5rem",
              fontWeight: "700",
              color: "white",
            }}
          >
            Yeni Maliyet Ekle
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "white",
              fontSize: "1.5rem",
              cursor: "pointer",
              width: "2rem",
              height: "2rem",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "2rem" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1.5rem",
            }}
          >
            {/* Atölye - EN ÜSTTE VE ZORUNLU */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Atölye *
              </label>
              <select
                value={selectedWorkshopId}
                onChange={(e) => handleWorkshopChange(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "0.125rem solid #e0e0e0",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                }}
              >
                <option value="">Önce atölye seçiniz...</option>
                {workshops.map((workshop) => (
                  <option key={workshop.workshopId} value={workshop.workshopId}>
                    {workshop.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Maliyet Kalemi - Atölye seçilince aktif olur */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Maliyet Kalemi *
              </label>
              <select
                value={formData.costItemId || ""}
                onChange={(e) => handleCostItemChange(e.target.value)}
                required
                disabled={!selectedWorkshopId || loadingItems}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "0.125rem solid #e0e0e0",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  background:
                    !selectedWorkshopId || loadingItems ? "#f5f5f5" : "white",
                  cursor:
                    !selectedWorkshopId || loadingItems
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <option value="">
                  {loadingItems
                    ? "Yükleniyor..."
                    : !selectedWorkshopId
                      ? "Önce atölye seçiniz"
                      : workshopCostItems.length === 0
                        ? "Bu atölyede tanımlı maliyet yok"
                        : "Seçiniz..."}
                </option>
                {workshopCostItems.map((item) => (
                  <option key={item.costItemId} value={item.costItemId}>
                    {item.itemName} - {item.costCategory?.categoryName} (
                    {formatCurrency(item.unitPrice, item.currency)})
                  </option>
                ))}
              </select>
              {selectedWorkshopId &&
                workshopCostItems.length === 0 &&
                !loadingItems && (
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "#999",
                      marginTop: "0.25rem",
                    }}
                  >
                    Bu atölyede henüz maliyet kalemi tanımlanmamış.
                  </p>
                )}
            </div>

            {/* Miktar 1 */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                {formData.quantity1Label || "Miktar"} (
                {formData.unit || "Birim"}) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    quantity: parseFloat(e.target.value) || 0,
                  }))
                }
                required
                placeholder="Miktar girin"
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "0.125rem solid #e0e0e0",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                }}
              />
            </div>

            {/* Miktar 2 - Sadece quantity2Label varsa göster */}
            {formData.quantity2Label && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  {formData.quantity2Label}{" "}
                  {formData.unit2 && `(${formData.unit2})`}
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.quantity2 || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      quantity2: parseFloat(e.target.value) || undefined,
                    }))
                  }
                  placeholder="Miktar girin"
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: "0.125rem solid #e0e0e0",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                  }}
                />
              </div>
            )}

            {/* Toplam Tutar (otomatik hesaplanıyor) */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Toplam Tutar
              </label>
              <div
                style={{
                  padding: "0.75rem",
                  background: "#f8f9fe",
                  borderRadius: "0.5rem",
                  fontSize: "1.125rem",
                  fontWeight: "700",
                  color: "#24365C",
                }}
              >
                {formatCurrency(
                  formData.totalCost || 0,
                  formData.currency || "TRY",
                )}
              </div>
            </div>

            {/* Notlar */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Notlar
              </label>
              <textarea
                value={formData.notes || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                rows={3}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: "0.125rem solid #e0e0e0",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                placeholder="İsteğe bağlı notlar..."
              />
            </div>
          </div>

          <div
            style={{
              marginTop: "2rem",
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "0.75rem 1.5rem",
                fontSize: "0.875rem",
                fontWeight: "600",
                border: "0.125rem solid #e0e0e0",
                background: "white",
                color: "#666",
                borderRadius: "0.5rem",
                cursor: "pointer",
              }}
            >
              İptal
            </button>
            <button
              type="submit"
              style={{
                padding: "0.75rem 2rem",
                fontSize: "0.875rem",
                fontWeight: "600",
                border: "none",
                background: "linear-gradient(135deg, #24365C 0%, #101B33 100%)",
                color: "white",
                borderRadius: "0.5rem",
                cursor: "pointer",
                boxShadow: "0 0.25rem 0.75rem rgba(36, 54, 92, 0.3)",
              }}
            >
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModelCosts;
